import { useMutation, useQuery, useQueryClient, type DefaultError, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { ApiError, endpoints, type Me, type TwoFactorConfirmBody, type TwoFactorDisableBody } from './api';

const SESSION_KEY = ['session'] as const;

/**
 * The staff levels, in the order they include one another.
 *
 * The same ordering the API enforces, restated here only to decide what to
 * render. The server refuses regardless; showing someone a button that always
 * answers 403 is a different defect from letting them press it.
 */
export const LEVELS = ['support', 'admin', 'superadmin'] as const;
export type Level = (typeof LEVELS)[number];

const RANK: Record<string, number> = { support: 1, admin: 2, superadmin: 3 };

export interface Session {
    me: Me | null;
    isLoading: boolean;
    /** True when this account is platform staff at all. */
    isStaff: boolean;
    atLeast: (level: Level) => boolean;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: SESSION_KEY,
        queryFn: endpoints.me,
        retry: (failureCount, error) => !(error instanceof ApiError && error.isUnauthenticated) && failureCount < 2,
        staleTime: 30_000,
    });

    useEffect(() => {
        const onExpired = () => queryClient.setQueryData(SESSION_KEY, null);
        window.addEventListener('session:expired', onExpired);
        return () => window.removeEventListener('session:expired', onExpired);
    }, [queryClient]);

    const me = data ?? null;
    // Empty for anyone who is not staff, which is every seller. Used only to
    // decide what is worth rendering: the API refuses regardless, and a client
    // check is never the enforcement point.
    const level = (me?.platformLevel ?? '') as Level | '';

    const value: Session = {
        me,
        isLoading,
        isStaff: Boolean(me?.platformAdmin),
        atLeast: (wanted: Level) => Boolean(level) && (RANK[level] ?? 0) >= (RANK[wanted] ?? 0),
    };

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
    const session = useContext(SessionContext);
    if (!session) {
        throw new Error('useSession must be used inside SessionProvider');
    }
    return session;
}

/**
 * A write that names the staff level its endpoint requires, in the data hook
 * that performs it and nowhere else. A screen draws the action only when
 * `allowed` is true; the server still refuses everyone below it.
 * `npm run check` holds the level to the endpoint handler's own check.
 */
export function usePermittedMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
    level: Level,
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> & { allowed: boolean } {
    const { atLeast } = useSession();
    const mutation = useMutation(options);
    return { ...mutation, allowed: atLeast(level) };
}

export function useLogin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.login,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

/** The code that finishes a sign-in waiting for its second step. */
export function useCompleteSecondFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.completeSecondFactor,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

/**
 * Two-step sign-in, which staff turn on or off from Hesabım as anyone does
 * (docs/07 §4): a new authenticator secret, then the first code that turns it
 * on and returns the recovery codes, shown once. The person's own account, so
 * plain mutations; on and off both change what the session says.
 */
export function useBeginTwoFactor() {
    return useMutation({ mutationFn: () => endpoints.beginTwoFactor() });
}

export function useConfirmTwoFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TwoFactorConfirmBody) => endpoints.confirmTwoFactor(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useDisableTwoFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TwoFactorDisableBody) => endpoints.disableTwoFactor(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useLogout() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.logout,
        onSuccess: () => queryClient.setQueryData(SESSION_KEY, null),
    });
}
